import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useAccountState } from 'contexts/account/account-context';

import { Box, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import Autocomplete from 'components/autocomplete';
import FlexBox from 'components/flex-box';
import { AccountCompany } from 'contexts/account/types/account-company.type';
import { useIntl } from 'react-intl';
import { UserType } from 'types/shared/user-type.enum';
import { createFuzzyFilterOptions } from 'utils/fuzzy-filter';
import googleTagManager from 'utils/google-tag-manager';
import { saveState } from 'utils/storage';
import ChangeLanguage from 'views/settings/components/change-language';

type SelectCompanyDialogProps = {};

const SelectCompanyDialog: React.FC<SelectCompanyDialogProps> = () => {
  const history = useHistory();
  const intl = useIntl();
  const { account, setAccount } = useAccountState();

  const [selectedAccount, setSelectedAccount] = useState<AccountCompany | null>(null);

  const setCompany = (company: AccountCompany | null) => {
    if (company) {
      saveState({ key: 'companyId', state: company.companyId });

      setAccount(company.companyId);

      googleTagManager.push({
        event: 'select_company',
        companyId: company.companyId,
        companyType: company.companyType,
        marketId: company.marketId,
        marketName: company.marketName,
        marketIndustry: company.aquaSpecies,
        userType: (account.currentUser?.userType as UserType) || '',
        pageUrl: window.location.pathname,
      });

      setSelectedAccount(company);
      history.replace(window.location.pathname);
    }
  };

  useEffect(() => {
    if (account.companies.length === 1 && !!account.currentUser) {
      setCompany(account.companies[0]);
    }
  }, [account.companies, account.currentUser]);

  return (
    <Dialog
      sx={{
        backgroundImage:
          'linear-gradient(to top, #012231, #012231, #012231, #012231, #012231, #012737, #002b3e, #003044, #003a54, #004563, #005073, #005b83)',
      }}
      fullWidth
      maxWidth="xs"
      open={true}
      onClose={() => null}>
      <Box>
        <Box textAlign={'center'}>
          <Typography variant="h3">{intl.formatMessage({ id: 'selectCompany.intro' })}</Typography>
          <Typography variant="body1">{intl.formatMessage({ id: 'selectCompany.subtitle' })}</Typography>
        </Box>
        <FlexBox paddingTop={2}>
          <Autocomplete
            value={selectedAccount}
            dark
            onChange={(_, company) => setCompany(company)}
            fullWidth
            label={intl.formatMessage({ id: 'general.company' })}
            noOptionsText={intl.formatMessage({ id: 'general.noResults' })}
            getOptionLabel={(company) => company.customerName}
            isOptionEqualToValue={(option, company) => option.companyId === company.companyId}
            filterOptions={createFuzzyFilterOptions((company) => `${company.customerErpId} ${company.customerName}`)}
            disableCloseOnSelect={false}
            options={account.companies || []}
          />
          <ChangeLanguage dark fullWidth />
        </FlexBox>
      </Box>
    </Dialog>
  );
};

export default SelectCompanyDialog;
